import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useNotification } from "../notifications/NotificationContext";
import { placeTypeRu } from "../utils/placeTypeRu";

const API_BASE = process.env.REACT_APP_API_URL || "";

const STATUS_LABEL = {
    moderation_required: "На модерации",
    discarded: "Отменено пользователем",
};

function boolRu(v) {
    return v ? "Да" : "Нет";
}

function FieldRow({ label, left, right }) {
    const changed = JSON.stringify(left) !== JSON.stringify(right);
    return (
        <div className={`draft-compare-row${changed ? " draft-compare-row--changed" : ""}`}>
            <div><strong>{label}</strong></div>
            <div>{left ?? "—"}</div>
            <div>{right ?? "—"}</div>
        </div>
    );
}

export default function SpotSuggestionDraftPage({ draftId }) {
    const { showNotification } = useNotification();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [discardBusy, setDiscardBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const { data: res } = await axios.get(`${API_BASE}/api/spots/drafts/my/${draftId}`, { withCredentials: true });
            setData(res);
        } catch (e) {
            setError(e?.response?.data?.error || "Не удалось загрузить предложение.");
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [draftId]);

    useEffect(() => {
        load();
    }, [load]);

    const discard = async () => {
        if (!draftId || !window.confirm("Отменить это предложение? Запись останется со статусом «отменено».")) return;
        setDiscardBusy(true);
        try {
            await axios.post(`${API_BASE}/api/spots/drafts/my/${draftId}/discard`, {}, { withCredentials: true });
            showNotification({ type: "success", text: "Предложение отменено." });
            await load();
        } catch (e) {
            showNotification({ type: "error", text: e?.response?.data?.error || "Не удалось отменить." });
        } finally {
            setDiscardBusy(false);
        }
    };

    if (loading) return <p className="page-status-message">Загрузка...</p>;
    if (error) return <p className="form-message">{error}</p>;
    if (!data?.draft || !data?.original) return <p className="page-status-message">Предложение не найдено.</p>;

    const draft = data.draft;
    const original = data.original;
    const st = draft.moderationStatus || "";
    const canEditStill = st === "moderation_required";

    const fishJoin = (list) => (Array.isArray(list) ? list.map((f) => f.name).join(", ") : "—");

    return (
        <div>
            <p className="entity-popup__muted" style={{ marginBottom: 12 }}>
                <Link to="/profile/suggestions">← К списку предложений</Link>
                {" · "}
                <Link to={`/spots/${draft.spotId}`}>Страница точки</Link>
            </p>
            <h2 className="page-title">Предложение правки #{draft.id}</h2>
            <p>
                Статус: <strong>{STATUS_LABEL[st] || st || "—"}</strong>
            </p>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                {canEditStill && (
                    <Link className="toolbar-btn" style={{ textDecoration: "none" }} to={`/spots/${draft.spotId}/edit-suggestion`}>
                        Редактировать предложение
                    </Link>
                )}
                {canEditStill && (
                    <button type="button" className="ghost-btn" disabled={discardBusy} onClick={discard}>
                        {discardBusy ? "Отмена…" : "Отменить предложение"}
                    </button>
                )}
            </div>

            <div className="draft-compare-head">
                <span />
                <strong>Исходная версия</strong>
                <strong>Ваше предложение</strong>
            </div>
            <div className="draft-compare">
                <FieldRow label="Описание" left={original.description} right={draft.description} />
                <FieldRow label="Дорога" left={original.roadType} right={draft.roadType} />
                <FieldRow label="Максимум рыбаков" left={original.capacityPersons} right={draft.capacityPersons} />
                <FieldRow label="Берег" left={boolRu(original.fishingFromShore)} right={boolRu(draft.fishingFromShore)} />
                <FieldRow label="Лодка" left={boolRu(original.fishingFromBoat)} right={boolRu(draft.fishingFromBoat)} />
                <FieldRow label="Ночная" left={boolRu(original.nightFishingAllowed)} right={boolRu(draft.nightFishingAllowed)} />
                <FieldRow label="Палатка" left={boolRu(original.tentAllowed)} right={boolRu(draft.tentAllowed)} />
                <FieldRow label="Детям" left={boolRu(original.suitableForChildren)} right={boolRu(draft.suitableForChildren)} />
                <FieldRow label="Парковка" left={boolRu(original.hasParking)} right={boolRu(draft.hasParking)} />
                <FieldRow label="Укрытие" left={boolRu(original.hasShelter)} right={boolRu(draft.hasShelter)} />
                <FieldRow label="Зимовальная яма" left={boolRu(original.isWinterHole)} right={boolRu(draft.isWinterHole)} />
                <FieldRow label="Нерест" left={boolRu(original.isSpawningArea)} right={boolRu(draft.isSpawningArea)} />
                <FieldRow label="Тип берега" left={original.bankType || "—"} right={draft.bankType || "—"} />
                <FieldRow label="Макс. глубина (м)" left={original.maxDepth ?? "—"} right={draft.maxDepth ?? "—"} />
                <FieldRow label="Течение" left={original.currentStrength} right={draft.currentStrength} />
                <FieldRow
                    label="Водоём"
                    left={original.place?.name ? `${original.place.name} (${placeTypeRu(original.place?.type)})` : "—"}
                    right={draft.place?.name ? `${draft.place.name} (${placeTypeRu(draft.place?.type)})` : "—"}
                />
                <FieldRow label="Рыбы" left={fishJoin(original.fish)} right={fishJoin(draft.fish)} />
                <FieldRow label="Широта" left={original.latitude} right="— (меняют модераторы)" />
                <FieldRow label="Долгота" left={original.longitude} right="—" />
            </div>

            <h4 style={{ marginTop: 20 }}>Новые фото в предложении</h4>
            {!(draft.newPhotos || []).length && <p className="entity-popup__muted">Не добавлено</p>}
            {(draft.newPhotos || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {(draft.newPhotos || []).map((ph, idx) => (
                        <figure key={`${ph.path}-${idx}`} className="page-item">
                            <img alt="" src={`${API_BASE}${ph.path}`} width={160} height={120} decoding="async" style={{ maxWidth: 160, height: "auto", aspectRatio: "4 / 3", objectFit: "cover" }} />
                            <figcaption className="entity-popup__muted">{ph.type}</figcaption>
                        </figure>
                    ))}
                </div>
            )}
        </div>
    );
}
